<template>
  <section class="vlog-section">
    <div class="container">
      <div class="vlog-section__layout">
        <div class="vlog-section__filters">
          <VlogFilters class="vlog-section__filters-desktop" />
          <VlogFiltersMobile class="vlog-section__filters-mobile" />
        </div>
        <div>
          <div class="vlog-section__header">
            <div class="vlog-section__number">
              {{ vlogs.length }} {{ $t('common.reports.results') }}
            </div>
            <button class="vlog-section__show-all" @click="onClearFilters">
              {{ $t('common.reports.show_all') }}
            </button>
          </div>
          <TransitionGroup tag="div" class="vlog-section__cards" name="list">
            <VlogCard v-for="vlog in vlogs" :key="vlog.uuid" :vlog="vlog" class="vlog-section__card" />
          </TransitionGroup>
          <div v-if="vlogs.length === 0" class="vlog-section__no-results">
            <img
              width="74"
              height="74"
              src="~/assets/icons/icon-noresults.svg?url"
              alt=""
              class="vlog-section__no-results-image"
            />
            <p class="vlog-section__no-results-info">
              {{ $t('common.reports.no_results') }}
            </p>
            <BaseButton
              :title="$t('common.reset_filters')"
              outlined
              @click="clearFilters"
            >
              <template #text> {{ $t('common.reset_filters') }} </template>
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { SbVlog, Story } from '@/types'

const vlogsStore = useVlogsStore()
const { clearFilters } = vlogsStore

const onClearFilters = () => {
  clearFilters()
}

interface Props {
  vlogs: Story<SbVlog>[]
}

defineProps<Props>()
</script>

<style lang="scss" scoped>
.vlog-section {
  background-color: var(--paper-100);
  padding: 64px 0;

  @media (min-width: $breakpoint-md) {
    padding: 80px 0;
  }

  @media (min-width: $breakpoint-lg) {
    padding: 96px 0 128px;
  }

  &__layout {
    display: grid;
    grid-template-columns: 1fr;

    @media (min-width: $breakpoint-lg) {
      grid-template-columns: 255px 1fr;
    }
  }

  &__cards {
    position: relative;
  }

  &__card {
    margin-bottom: 24px;
    width: 100%;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 32px;

    @media (min-width: $breakpoint-lg) {
      margin-bottom: 40px;
    }
  }

  &__number {
    @include heading-subheading;
  }

  &__show-all {
    font-size: var(--font-size-paragraph-2xs);
    line-height: 21px;

    &:hover {
      color: var(--color-link);
    }
  }

  &__filters-desktop {
    display: none;

    @media (min-width: $breakpoint-lg) {
      display: block;
    }
  }

  &__filters-mobile {
    display: block;
    margin-bottom: 40px;

    @media (min-width: $breakpoint-lg) {
      display: none;
    }
  }

  &__no-results {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 12px;
    margin-top: 36px;
  }

  &__no-results-info {
    @include paragraph-m;

    margin-bottom: 0;
    font-weight: var(--font-weight-paragraph-bold);
  }
}
</style>
